import React, { useEffect, useMemo, useState } from "react";
import { X, Calendar, Users, ChevronRight, ChevronLeft, Check, MessageCircle, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, buildWaLink } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const formatIDR = (n) => `IDR ${Number(n).toLocaleString("id-ID")}`;

// Parse "1 Hari" / "2D1N" / "3D2N" -> nights count
const parseNights = (duration) => {
  if (!duration) return 0;
  const m = duration.match(/(\d+)\s*N/i);
  if (m) return parseInt(m[1], 10);
  if (/1\s*Hari/i.test(duration)) return 0;
  return 0;
};

const addDays = (dateStr, days) => {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const stepBg = (done, active) => {
  if (done) return "bg-[#0E9A57] text-white";
  if (active) return "bg-[#0C2230] text-white";
  return "bg-[#0C2230]/12 text-[#0C2230]/55";
};

const Stepper = ({ step, labels }) => (
  <div className="flex items-center gap-2 px-6 pt-5 md:px-10">
    {labels.map((label, i) => {
      const idx = i + 1;
      const done = idx < step;
      const active = idx === step;
      return (
        <React.Fragment key={label}>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold transition-colors ${stepBg(done, active)}`}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : idx}
            </span>
            <span className={`hidden text-xs font-semibold uppercase tracking-wider md:inline ${active || done ? "text-[#0C2230]" : "text-[#0C2230]/45"}`}>
              {label}
            </span>
          </div>
          {i < labels.length - 1 && <span className={`h-px flex-1 ${done ? "bg-[#0E9A57]" : "bg-[#0C2230]/15"}`} />}
        </React.Fragment>
      );
    })}
  </div>
);

const Field = ({ label, children, required }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[#0C2230]/70">
      {label} {required && <span className="text-[#DC2626]">*</span>}
    </span>
    {children}
  </label>
);

const inputCls = "w-full rounded-lg border border-[#0C2230]/15 bg-white px-3.5 py-2.5 text-sm text-[#0C2230] outline-none transition-colors focus:border-[#0E9A57] focus:ring-2 focus:ring-[#0E9A57]/15";

// ---------- Step components ----------
const Counter = ({ value, onChange, min = 0, testid }) => (
  <div className="flex items-stretch overflow-hidden rounded-lg border border-[#0C2230]/15">
    <button type="button" onClick={() => onChange(Math.max(min, value - 1))} className="px-3 text-lg font-bold text-[#0C2230] hover:bg-[#FBFAF6]">−</button>
    <input
      type="number" min={min} value={value}
      onChange={(e) => onChange(Math.max(min, parseInt(e.target.value || String(min), 10)))}
      data-testid={testid}
      className="w-full border-x border-[#0C2230]/10 bg-white px-2 py-2.5 text-center text-sm font-semibold text-[#0C2230] outline-none"
    />
    <button type="button" onClick={() => onChange(value + 1)} className="px-3 text-lg font-bold text-[#0C2230] hover:bg-[#FBFAF6]">+</button>
  </div>
);

const StepDates = ({ B, pkg, form, set, minDate, end_date, nights, totalPrice }) => (
  <div className="space-y-6">
    <Field label={B.startDate} required>
      <input
        type="date" min={minDate} value={form.start_date}
        onChange={(e) => set("start_date", e.target.value)}
        data-testid="booking-start-date" className={inputCls}
      />
    </Field>
    <Field label={B.endDate}>
      <input type="date" value={end_date} disabled className={inputCls + " bg-[#FBFAF6] cursor-not-allowed"} />
      <span className="mt-1 inline-block text-xs text-[#0C2230]/55">{nights === 0 ? "Same-day return" : `${nights} ${nights === 1 ? "night" : "nights"} · ${pkg.duration}`}</span>
    </Field>
    <div className="grid grid-cols-2 gap-4">
      <Field label={B.adults} required>
        <Counter value={form.adults} onChange={(v) => set("adults", v)} min={1} testid="booking-adults" />
      </Field>
      <Field label={`${B.children} ${B.child_age}`}>
        <Counter value={form.children} onChange={(v) => set("children", v)} min={0} testid="booking-children" />
      </Field>
    </div>
    <div className="flex items-center justify-between rounded-xl bg-[#FBFAF6] px-5 py-4">
      <span className="text-sm font-semibold text-[#0C2230]">{B.total}</span>
      <span className="font-[Fraunces] text-2xl font-bold text-[#0E9A57]">{formatIDR(totalPrice)}</span>
    </div>
  </div>
);

const StepInfo = ({ B, form, set }) => (
  <div className="grid gap-5 md:grid-cols-2">
    <Field label={B.name} required>
      <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="booking-name" className={inputCls} placeholder="John Doe" />
    </Field>
    <Field label={B.email} required>
      <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="booking-email" className={inputCls} placeholder="you@example.com" />
    </Field>
    <Field label={B.phone} required>
      <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="booking-phone" className={inputCls} placeholder="+62 813 9999 0000" />
    </Field>
    <Field label={B.country}>
      <input type="text" value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="booking-country" className={inputCls} placeholder="Indonesia / Singapore / Malaysia ..." />
    </Field>
    <div className="md:col-span-2">
      <Field label={B.notes}>
        <textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="booking-notes" className={inputCls + " resize-none"} placeholder="Diet, allergi, request khusus..." />
      </Field>
    </div>
  </div>
);

const StepReview = ({ B, pkg, form, end_date, nights, totalPrice }) => {
  const rows = [
    [B.package, `${pkg.title} · ${pkg.subtitle || ""}`],
    [B.dates, `${form.start_date} → ${end_date} (${nights === 0 ? "1 day" : `${nights + 1}D${nights}N`})`],
    [B.pax, `${form.adults} ${B.adults}${form.children ? ` + ${form.children} ${B.children}` : ""}`],
    [B.name, form.name],
    [B.email, form.email],
    [B.phone, form.phone],
    [B.country, form.country || "—"],
    [B.notes, form.notes || "—"],
  ];
  return (
    <div className="space-y-5">
      <h4 className="font-[Fraunces] text-xl text-[#0C2230]">{B.reviewTitle}</h4>
      <dl className="divide-y divide-[#0C2230]/10 rounded-xl border border-[#0C2230]/10 bg-[#FBFAF6]">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-start justify-between gap-4 px-5 py-3">
            <dt className="text-xs font-semibold uppercase tracking-wider text-[#0C2230]/55">{k}</dt>
            <dd className="max-w-[60%] text-right text-sm text-[#0C2230]">{v}</dd>
          </div>
        ))}
      </dl>
      <div className="flex items-center justify-between rounded-xl bg-gradient-to-br from-[#0C2230] to-[#0E9A57] px-5 py-4 text-white">
        <span className="text-sm font-semibold uppercase tracking-wider">{B.total}</span>
        <span className="font-[Fraunces] text-3xl font-bold">{formatIDR(totalPrice)}</span>
      </div>
      <p className="rounded-lg bg-[#FFF7E6] p-3 text-xs leading-relaxed text-[#7A5D00]">
        💡 {B.payOnlineSoon}. Setelah konfirmasi, tim kami akan kirim instruksi pembayaran via WhatsApp / Email.
      </p>
    </div>
  );
};

const StepSuccess = ({ B, booking, waMessage }) => (
  <div className="space-y-5 text-center">
    <div className="mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full bg-[#0E9A57] text-white shadow-lg">
      <Check className="h-8 w-8" />
    </div>
    <div>
      <h4 className="font-[Fraunces] text-2xl text-[#0C2230]">{B.success}</h4>
      <p className="mt-2 text-sm text-[#0C2230]/70">{B.successDesc}</p>
    </div>
    <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-[#FBFAF6] px-4 py-2 text-xs text-[#0C2230]/65">
      {B.bookingId}: <code className="font-mono font-bold text-[#0C2230]">{booking.id.slice(0, 8).toUpperCase()}</code>
    </div>
    <a
      href={buildWaLink(waMessage)} target="_blank" rel="noreferrer"
      data-testid="booking-wa-followup"
      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3.5 text-sm font-semibold text-white shadow-lg hover:bg-[#1ebe57]"
    >
      <MessageCircle className="h-4 w-4" /> {B.wa_followup}
    </a>
  </div>
);

const StepFooter = ({ step, B, canNext1, canNext2, onBack, onNext, onSubmit, submitting }) => (
  <div className="shrink-0 border-t border-[#0C2230]/10 bg-white px-6 py-4 md:px-10">
    <div className="flex items-center justify-between gap-3">
      <button
        type="button" onClick={onBack} data-testid="booking-back"
        className="inline-flex items-center gap-1 rounded-lg border border-[#0C2230]/15 bg-white px-4 py-2.5 text-sm font-semibold text-[#0C2230] hover:bg-[#FBFAF6]"
      >
        <ChevronLeft className="h-4 w-4" /> {step === 1 ? "Cancel" : B.back}
      </button>
      {step < 3 && (
        <button
          type="button"
          disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
          onClick={onNext} data-testid="booking-next"
          className="inline-flex items-center gap-1 rounded-lg bg-[#0E9A57] px-6 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-[#0c8a4d] hover:shadow-lg disabled:cursor-not-allowed disabled:bg-[#0C2230]/25 disabled:shadow-none"
        >
          {B.next} <ChevronRight className="h-4 w-4" />
        </button>
      )}
      {step === 3 && (
        <button
          type="button" disabled={submitting} onClick={onSubmit} data-testid="booking-submit"
          className="inline-flex items-center gap-2 rounded-lg bg-[#0E9A57] px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:bg-[#0c8a4d] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          {submitting ? B.submitting : B.confirm}
        </button>
      )}
    </div>
  </div>
);

const buildWaMessage = (b) =>
  `Halo CEO Travel, saya baru saja membuat booking via website.\n\n*Booking ID:* ${b.id.slice(0, 8).toUpperCase()}\n*Paket:* ${b.package_title}\n*Tanggal:* ${b.start_date} → ${b.end_date}\n*Peserta:* ${b.adults} dewasa${b.children ? `, ${b.children} anak` : ""}\n*Total:* ${formatIDR(b.total_price)}\n*Nama:* ${b.customer_name}\n\nMohon konfirmasi dan info pembayaran. Terima kasih.`;

export const BookingFlow = ({ pkg, onClose }) => {
  const { t, lang } = useLang();
  const B = t.booking;
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [createdBooking, setCreatedBooking] = useState(null);

  const nights = useMemo(() => parseNights(pkg?.duration), [pkg?.duration]);
  const today = new Date().toISOString().slice(0, 10);
  const minDate = addDays(today, 3); // require ≥ 3 days lead time

  const [form, setForm] = useState({
    start_date: minDate,
    adults: 2,
    children: 0,
    name: "",
    email: "",
    phone: "",
    country: "",
    notes: "",
  });

  const end_date = useMemo(() => addDays(form.start_date, nights), [form.start_date, nights]);
  const totalPrice = useMemo(() => {
    const adultPrice = pkg?.price_from || 0;
    const childPrice = Math.round(adultPrice * 0.5);
    return form.adults * adultPrice + form.children * childPrice;
  }, [pkg?.price_from, form.adults, form.children]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  if (!pkg) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canNext1 = form.start_date && form.adults >= 1;
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
  const canNext2 = form.name.trim() && validEmail && form.phone.trim();

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        package_id: pkg.id,
        package_title: `${pkg.title} – ${pkg.subtitle || ""}`.trim(),
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        customer_country: form.country.trim() || null,
        start_date: form.start_date,
        end_date,
        adults: form.adults,
        children: form.children,
        total_price: totalPrice,
        currency: "IDR",
        notes: form.notes.trim() || null,
        lang,
      });
      setCreatedBooking(data);
      setStep(4);
    } catch (e) {
      toast.error("Gagal menyimpan booking. Coba lagi.");
    } finally {
      setSubmitting(false);
    }
  };

  const waMessage = createdBooking ? buildWaMessage(createdBooking) : "";

  const handleBack = () => (step === 1 ? onClose() : setStep((s) => s - 1));
  const handleNext = () => setStep((s) => s + 1);

  return (
    <div
      data-testid="booking-flow-modal"
      className="fixed inset-0 z-[110] flex items-end justify-center bg-black/65 backdrop-blur-sm md:items-center"
      onClick={onClose}
    >
      <div
        className="relative flex h-[95vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl md:h-auto md:max-h-[90vh] md:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="booking-close" onClick={onClose}
          className="absolute right-5 top-5 z-20 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#0C2230] shadow-md ring-1 ring-black/5 hover:bg-[#FBFAF6]"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Header */}
        <div className="shrink-0 border-b border-[#0C2230]/8 bg-[#FBFAF6] px-6 py-4 md:px-10">
          <h3 className="font-[Fraunces] text-2xl font-medium text-[#0C2230]">{B.title}</h3>
          <p className="mt-0.5 text-sm text-[#0C2230]/65">{pkg.title} {pkg.subtitle ? `· ${pkg.subtitle}` : ""}</p>
        </div>

        <Stepper step={step} labels={[B.step1, B.step2, B.step3, B.step4]} />

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6 md:px-10 md:py-8">
          {step === 1 && <StepDates B={B} pkg={pkg} form={form} set={set} minDate={minDate} end_date={end_date} nights={nights} totalPrice={totalPrice} />}
          {step === 2 && <StepInfo B={B} form={form} set={set} />}
          {step === 3 && <StepReview B={B} pkg={pkg} form={form} end_date={end_date} nights={nights} totalPrice={totalPrice} />}
          {step === 4 && createdBooking && <StepSuccess B={B} booking={createdBooking} waMessage={waMessage} />}
        </div>

        {/* Footer */}
        {step < 4 && (
          <StepFooter
            step={step} B={B}
            canNext1={canNext1} canNext2={canNext2}
            onBack={handleBack} onNext={handleNext}
            onSubmit={submit} submitting={submitting}
          />
        )}
      </div>
    </div>
  );
};

export default BookingFlow;
